import { api } from '../lib/api'
import { useGatedBook } from './useGatedBook'
import type { ClubMark } from '../lib/types'

/**
 * The club's book of posted rounds, and nothing else: `GET /golf/rounds/feed`.
 *
 * Separate from `useClubhouseBook` — which reads the feed AND the register in
 * one shot because the clubhouse home is one surface — because The Lounge
 * renders only the comings and goings. Fetching a register it never prints
 * would be a second gated round-trip bought for nothing.
 *
 * The gate is the same, and so is the state shape, deliberately: both hooks
 * hand `BookGate` exactly `{ isAuthenticated, membershipRequired, error }`.
 * `useGatedBook` is that gate, and it is where the states are documented —
 * a 403 `membership_required` is NOT an error but the club ANSWERING, and the
 * feed is membership-gated while every clubhouse PAGE is not, so a lapsed
 * member sees the room and the club's own plate where the book would be.
 *
 * ⚠️ The feed is VIEWER-DEPENDENT (`is_you`), so its response must never be
 * cached by anything between the club and the reader.
 */

/**
 * The scorekeeper, as a feed row names them (#shared_member_json).
 *
 * NAMED, NEVER IDENTIFIED: no account `id` and no live `handicap_index`, which
 * is the same narrowing the public share page applies. Both fields were on
 * this type until 2026-08-22 — `round_feed_json` rendered the keeper through
 * `#golf_member_json` while every player row beside them carried neither — and
 * the server closed that gap deliberately. No screen ever read either one.
 *
 * Declared here rather than in `lib/types.ts`: that file is copied verbatim
 * into the Caddie app by `bin/mobile contract`, and the feed is a web
 * clubhouse read the phone never makes.
 */
export interface FeedScorekeeper {
  display_name: string
  club_mark: ClubMark
}

/**
 * One player's line on a posted round (#feed_player_json).
 *
 * Carries NO id and no address — a guest never asked to be in the club's feed,
 * and the most it may say about anyone is the name on the card that day.
 * `is_you` is the one exception and it is not a widening: it answers "is this
 * row the reader" without saying who any other row is, and the server computes
 * it from the signed-in member's id. A guest is always false.
 */
export interface FeedPlayer {
  display_name: string
  guest: boolean
  is_you: boolean
  total_strokes: number | null
  score_differential: number | null
}

/** A posted round as the clubhouse feed sees it (#round_feed_json). */
export interface FeedRound {
  id: string
  course_key: string
  course_name: string
  played_on: string | null
  posted_at: string | null
  holes_count: number
  /**
   * Par of the holes THIS ROUND PLAYED — a nine's par is the nine's. ALL OR
   * NOTHING: `null` when any played hole has no par, because the one thing a
   * client does with this is subtract it from a gross. Never compute a
   * substitute.
   */
  round_par: number | null
  scorekeeper: FeedScorekeeper | null
  players: FeedPlayer[]
}

interface RoundsFeedResponse {
  rounds: FeedRound[]
}

export const FEED_PATH = '/golf/rounds/feed'

/** One empty book, not a fresh one per render — see `useGatedBook`'s note. */
const NO_ROUNDS: FeedRound[] = []

export function useRoundsFeed(enabled: boolean) {
  const book = useGatedBook<FeedRound[]>({
    key: FEED_PATH,
    enabled,
    read: () => api.get<RoundsFeedResponse>(FEED_PATH).then((feed) => feed.rounds),
    whenFaulty: 'Could not open the club’s book.',
  })

  return {
    rounds: book.data ?? NO_ROUNDS,
    isLoading: book.isLoading,
    error: book.error,
    membershipRequired: book.membershipRequired,
    refresh: book.refresh,
  }
}
